/* eslint-disable @typescript-eslint/no-deprecated */

import React from 'react'
import styled, { useTheme } from 'styled-components'
import { LinkRounded } from '@mui/icons-material'
import { Card, CardHeader, CardContent } from '../../buildingBlocks'
import { Body, SmallHeading, SubHeading } from '../../buildingBlocks/Typography'
import { Organization, industryCategoryToIndustryCategoryTitle } from '../../domain'
import { IndustryIcon } from '../IndustryIcon/IndustryIcon'
import { getCountryNameFromISO } from '../../utils/formatting/countryCode'

interface Props {
  organization: Organization
  flagComponent?: React.ReactNode
}

export const OrganizationOverview: React.FC<Props> = ({ organization, flagComponent }) => {
  const { colors } = useTheme()

  const { bio, industryCategory, websiteUrl, headquarters } = organization

  return (
    <Card>
      <CardHeader helpText={HELP_TEXT}>
        <SubHeading mb={0}>{TITLE}</SubHeading>
      </CardHeader>
      <CardContent>
        {bio ? (
          <>
            <Body color={colors.dark}>{bio.description || BIO_NOT_AVAILABLE}</Body>
            <Item>
              <StyledSmallHeading as="h3">Source</StyledSmallHeading>
              {bio.sourceUrl ? <LinkItem url={bio.sourceUrl} text={bio.source} /> : <Body m={0}>{bio.source}</Body>}
            </Item>
          </>
        ) : (
          <Item>
            <StyledSmallHeading as="h3">Bio</StyledSmallHeading>
            <Body m={0}>{NOT_AVAILABLE}</Body>
          </Item>
        )}
        <Item>
          <StyledSmallHeading as="h3">Industry</StyledSmallHeading>
          {industryCategory ? (
            <Row>
              <IndustryIcon
                industryCategory={industryCategory}
                imageStyle={{ height: '3rem', width: '3rem', marginRight: '1rem' }}
              />
              <Body m={0}>{industryCategoryToIndustryCategoryTitle(industryCategory)}</Body>
            </Row>
          ) : (
            <Body m={0}>{NOT_AVAILABLE}</Body>
          )}
        </Item>
        <Item>
          <StyledSmallHeading as="h3">Head office</StyledSmallHeading>
          {headquarters && headquarters.isoCountryCode2 && getCountryNameFromISO(headquarters.isoCountryCode2) ? (
            <Row>
              {flagComponent}
              <Body m={0}>{getCountryNameFromISO(headquarters.isoCountryCode2)}</Body>
            </Row>
          ) : (
            <Body m={0}>{NOT_AVAILABLE}</Body>
          )}
        </Item>
        <Item>
          <StyledSmallHeading as="h3">Website</StyledSmallHeading>
          {websiteUrl ? <LinkItem url={websiteUrl} text={VISIT_COMPANY} /> : <Body m={0}>{NOT_AVAILABLE}</Body>}
        </Item>
      </CardContent>
    </Card>
  )
}

const LinkItem: React.FC<{ url: string; text: string }> = ({ url, text }) => {
  return (
    <Container>
      <LinkIcon className="icon" />
      <LinkItemText as="a" href={url} target="_blank" rel="noopener noreferrer">
        {text}
      </LinkItemText>
    </Container>
  )
}

const Row = styled.div`
  display: flex;
  align-items: center;
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const LinkIcon = styled(LinkRounded)`
  color: ${({ theme }) => theme.colors.secondary.medium};
  margin-right: 1rem;
`

const LinkItemText = styled(Body)`
  color: ${({ theme }) => theme.colors.link.dark};
  border-bottom: 1px solid ${({ theme }) => theme.colors.link.dark};
  margin: 0;
`

const Item = styled.div`
  margin: 2rem 0;
  display: flex;
  flex-direction: column;
  align-items: left;
`

const StyledSmallHeading = styled(SmallHeading)`
  margin: 0 0 1rem;
`

const BIO_NOT_AVAILABLE = 'Bio not available'
const NOT_AVAILABLE = 'Not available'
const TITLE = 'About this company'
const HELP_TEXT = `This tells you more about a specific company you have in your portfolio.

It includes a short bio of the company and a link to their website.`
const VISIT_COMPANY = 'Visit Company Website'
